import { useState } from 'react';
import { Link } from 'react-router-dom';
import { products, productCategories } from '@/mocks/products';
import { useCart } from '@/hooks/useCart';
import CartDrawer from '@/components/feature/CartDrawer';
import ProductCard from '@/pages/cardapio/components/ProductCard';
import ProductDetailPanel from '@/pages/cardapio/components/ProductDetailPanel';

export default function Cardapio() {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const { addItem, openCart, totalItems } = useCart();

  const filtered = activeCategory === 'Todos'
    ? products
    : products.filter((p) => p.category === activeCategory);

  const product = products.find((p) => p.id === selectedProduct);

  const getQuantity = (id: string) => quantities[id] || 1;

  const setQuantity = (id: string, qty: number) => {
    if (qty < 1) qty = 1;
    if (qty > 99) qty = 99;
    setQuantities((prev) => ({ ...prev, [id]: qty }));
  };

  const handleAddToCart = (id: string, name: string) => {
    const qty = getQuantity(id);
    addItem(id, name, qty);
    setQuantities((prev) => ({ ...prev, [id]: 1 }));
  };

  const countFor = (cat: string) =>
    cat === 'Todos' ? products.length : products.filter((p) => p.category === cat).length;

  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-secondary-500/20 bg-background-50/85 backdrop-blur-xl">
        <div className="flex items-center justify-between px-5 py-4 md:px-10 lg:px-14">
          <Link to="/" className="group flex items-baseline gap-2.5">
            <span className="font-display text-xl leading-none text-foreground-900 md:text-2xl">
              Naturalle
            </span>
            <span className="hidden font-label text-[10px] font-medium uppercase tracking-[0.35em] text-secondary-500 sm:inline-block">
              Sanduíches
            </span>
          </Link>

          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-2 whitespace-nowrap font-label text-sm font-medium text-foreground-500 transition-colors hover:text-secondary-400"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar
            </Link>
            <button
              onClick={openCart}
              aria-label="Abrir carrinho"
              className="relative flex h-10 w-10 items-center justify-center rounded-full border border-secondary-500/30 text-foreground-600 transition-colors hover:border-secondary-500 hover:text-secondary-400 cursor-pointer"
            >
              <i className="ri-shopping-bag-3-line text-lg" />
              {totalItems > 0 && (
                <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-accent-500 text-[10px] font-bold text-white">
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-6xl px-5 md:px-10 lg:px-14">
          {/* Hero */}
          <div className="mb-12 md:mb-16">
            <span className="mb-5 inline-flex items-center gap-3 font-label text-[11px] font-medium uppercase tracking-[0.3em] text-secondary-500">
              <span className="h-px w-8 bg-secondary-500/60" />
              Cardápio
            </span>
            <h1 className="mb-5 font-display text-4xl leading-[1.05] text-foreground-950 md:text-5xl lg:text-6xl">
              Sabores que
              <br />
              <span className="italic text-accent-400">alimentam</span> o seu dia
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-foreground-500 md:text-lg">
              Escolha as quantidades de cada sabor, monte seu pedido e envie tudo de uma vez pelo WhatsApp.
            </p>
            <p className="mt-3 flex items-center gap-2 text-xs text-foreground-400 md:text-sm">
              <i className="ri-information-line text-sm" />
              Imagens meramente ilustrativas. O produto pode variar em aparência.
            </p>
          </div>

          {/* Category filters */}
          <div className="mb-12 flex flex-wrap items-center gap-2 md:mb-16">
            {productCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap rounded-full px-5 py-2.5 font-label text-sm font-medium transition-all duration-300 cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-accent-500 text-white'
                    : 'border border-background-300/70 text-foreground-500 hover:border-secondary-500/50 hover:text-secondary-400'
                }`}
              >
                {cat}
                <span className={activeCategory === cat ? 'ml-2 text-white/70' : 'ml-2 text-foreground-400'}>
                  {countFor(cat)}
                </span>
              </button>
            ))}
          </div>

          {/* Product grid */}
          {filtered.length === 0 ? (
            <div className="py-20 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-background-200/70 bg-background-100">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-lg text-foreground-500">Nenhum produto nesta categoria ainda.</p>
              <p className="mt-1 text-sm text-foreground-400">Em breve teremos novidades!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:gap-6 lg:grid-cols-4">
              {filtered.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  quantity={getQuantity(p.id)}
                  isSelected={selectedProduct === p.id}
                  onSelect={() => setSelectedProduct(selectedProduct === p.id ? null : p.id)}
                  onQuantity={(qty) => setQuantity(p.id, qty)}
                  onAddToCart={() => handleAddToCart(p.id, p.name)}
                />
              ))}
            </div>
          )}

          {/* Detail panel */}
          {selectedProduct && product && (
            <ProductDetailPanel
              product={product}
              quantity={getQuantity(product.id)}
              onQuantity={(qty) => setQuantity(product.id, qty)}
              onAddToCart={() => handleAddToCart(product.id, product.name)}
              onClose={() => setSelectedProduct(null)}
            />
          )}
        </div>
      </section>

      <CartDrawer />
    </div>
  );
}